import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connectSocket, disconnectSocket } from '../services/socketService';
import { addNotification, setUnreadCount, updateNotificationRead, setAllRead, removeNotification } from '../redux/slices/notification';
import { showAlert } from '../redux/slices/alerts';
import { getUnreadCount } from '../redux/ducks/notification';
import { isLoggedIn } from '../redux/slices/auth';
import { setConfig } from '../redux/slices/config';

const useSocket = () => {
    const dispatch = useDispatch();
    const userId = useSelector((state) => state.auth.userId);
    const loggedOut = useSelector((state) => state.auth.isLogged === false);
    const badgeUserRef = useRef(null);

    useEffect(() => {
        if (loggedOut) {
            badgeUserRef.current = null;
            return;
        }
        if (!userId || badgeUserRef.current === userId) return;
        badgeUserRef.current = userId;
        dispatch(getUnreadCount());
    }, [userId, loggedOut, dispatch]);

    useEffect(() => {
        if (loggedOut) {
            disconnectSocket();
            return undefined;
        }
        if (!userId) return undefined;

        const socket = connectSocket(userId);

        const onNew = (notification) => {
            dispatch(addNotification(notification));
            dispatch(showAlert({ severity: 'info', message: notification.subject }));
        };
        const onUnread = ({ count }) => {
            dispatch(setUnreadCount({ count }));
        };
        const onAuthRevalidate = () => {
            dispatch(isLoggedIn());
        };

        const onRead = (payload) => {
            const ids = payload?.ids?.length ? payload.ids : [payload?._id];
            ids.filter(Boolean).forEach((id) => dispatch(updateNotificationRead({ id })));
        };
        const onReadAll = () => {
            dispatch(setAllRead());
        };
        const onDeleted = (payload) => {
            if (!payload?._id) return;
            dispatch(removeNotification({ id: payload._id }));
        };
        const onSystemMessage = ({ message }) => {
            dispatch(setConfig({ config: { message } }));
        };

        socket.on('notification:new', onNew);
        socket.on('notification:unreadCount', onUnread);
        socket.on('auth:revalidate', onAuthRevalidate);
        socket.on('notification:read', onRead);
        socket.on('notification:read-all', onReadAll);
        socket.on('notification:deleted', onDeleted);
        socket.on('systemConfig:message', onSystemMessage);

        return () => {
            socket.off('notification:new', onNew);
            socket.off('notification:unreadCount', onUnread);
            socket.off('auth:revalidate', onAuthRevalidate);
            socket.off('notification:read', onRead);
            socket.off('notification:read-all', onReadAll);
            socket.off('notification:deleted', onDeleted);
            socket.off('systemConfig:message', onSystemMessage);
        };
    }, [userId, loggedOut, dispatch]);
};

export default useSocket;
